import * as THREE from "three";
import { PointTree, type Scorer } from "./score.ts";
import type { Target } from "./targets.ts";

/** A closed surface to judge. `centroid` is its volume centroid. */
export interface Shape {
  positions: Float32Array;
  index: Uint32Array;
  areas: Float32Array;
  centroid: THREE.Vector3;
}

export type CriterionId =
  | "silhouette"
  | "balance"
  | "contour"
  | "likeness"
  | "symmetry"
  | "waist"
  | "hole"
  | "ears"
  | "efficiency"
  | "wish";

/**
 * A customer's particular wish for the piece, judged as a bonus: how far the piece goes from
 * the usual target toward the customer's own variant of it (which is the target judged and
 * shown), on the measure the wish is about.
 */
export interface Wish {
  /**
   * Length over width, the waist's depth, the hole's area, how far apart the legs stand, how
   * deep the grooves between coils are cut, how far ears stand up on top, how deep the front
   * outline's waist is, how far apart the holes lie, or how square it is from above.
   */
  measure: "aspect" | "waist" | "hole" | "split" | "coils" | "ears" | "neck" | "spread" | "square";
  /** Short name, as in "こだわり: 大きな取っ手". */
  label: string;
  /** Advice shown when the wish is the weakest point. */
  hint: string;
}

interface Criterion {
  label: string;
  /** Main gauges (the shape's three and its feature) add up to 100 points; bonuses come on top. */
  bonus: boolean;
  max: number;
  /** Comments for a gauge filled ≥ 99 %, ≥ 70 %, ≥ 40 % and below; {n} is the stroke count. */
  comments: [string, string, string, string];
  /** Advice shown when this is the weakest point. */
  hint: string;
}

const CRITERIA: Record<CriterionId, Criterion> = {
  silhouette: {
    label: "シルエット",
    bonus: false,
    max: 30,
    comments: [
      "シルエットは文句なし！",
      "シルエットがよく似てる！",
      "シルエットに雰囲気が出てきた",
      "シルエットはこれから",
    ],
    hint: "正面・横・上から見比べてみよう",
  },
  balance: {
    label: "重心・プロポーション",
    bonus: false,
    max: 20,
    comments: [
      "重心も縦横比もばっちり！",
      "どっしり具合が近い！",
      "プロポーションは惜しいところ",
      "重さの偏りがまだ違うみたい",
    ],
    hint: "重さがどちらの端に寄っているか意識してみよう",
  },
  contour: {
    label: "輪郭",
    bonus: false,
    max: 25,
    comments: [
      "輪郭もばっちり！",
      "輪郭がきれいに追えてる",
      "輪郭はもうひと息",
      "輪郭はこれから",
    ],
    hint: "「平ら」や「ならす」で表面を整えてみよう",
  },
  likeness: {
    label: "そっくりボーナス",
    bonus: true,
    max: 30,
    comments: [
      "細かいところまでそっくり！",
      "かなりそっくり！",
      "ちょっとそっくり",
      "ゲージ満タンの先でもらえる",
    ],
    hint: "",
  },
  // Label and hint are replaced per target by the mirror plane in use (see MIRRORS).
  symmetry: {
    label: "",
    bonus: true,
    max: 10,
    comments: [
      "バランスが完璧！",
      "よくそろってる",
      "少し偏りぎみ",
      "だいぶ偏ってるかも",
    ],
    hint: "",
  },
  waist: {
    label: "くびれ",
    bonus: false,
    max: 25,
    comments: [
      "くびれの位置も深さもそっくり！",
      "くびれがいい感じ！",
      "くびれはもう少し",
      "くびれはまだ見当たらないかも",
    ],
    hint: "くびれの位置と深さを意識してみよう",
  },
  hole: {
    label: "穴の配置",
    bonus: false,
    max: 25,
    comments: [
      "穴の位置も大きさもぴったり！",
      "穴の配置がきれい！",
      "穴の位置はもう少し",
      "穴の位置と大きさはこれから",
    ],
    hint: "穴の位置と大きさを目標に合わせてみよう",
  },
  ears: {
    label: "耳",
    bonus: false,
    max: 25,
    comments: [
      "二つのお耳がぴんと立ってる！",
      "耳の形がよく見えてきた！",
      "お耳が少しずつ出てきた",
      "頭の上にお耳を作ってみよう",
    ],
    hint: "頭の上を二か所つまんで、お耳を作ってみよう",
  },
  efficiency: {
    label: "手数ボーナス",
    bonus: true,
    max: 20,
    comments: [
      "{n}手でここまで！ 超すごい！",
      "{n}手、手際がいい！",
      "{n}手、ていねいにこねた",
      "{n}手、じっくりこねた力作",
    ],
    hint: "大きなブラシで大胆にこねると手数が減るかも",
  },
  // Label and hint come from the customer's wish.
  wish: {
    label: "こだわり",
    bonus: true,
    max: 20,
    comments: [
      "ご希望どおり！",
      "ご希望にかなり近い",
      "ご希望まであと少し",
      "ご希望はこれから",
    ],
    hint: "",
  },
};

/** Gauges of the shape as a whole; each target adds its feature. */
const SHAPE_IDS: CriterionId[] = ["silhouette", "balance", "contour"];
/** Said of the balance and stroke bonuses while the feature they wait for is missing. */
const FEATURE_PENDING = {
  waist: "くびれができたら数えるよ",
  hole: "穴の配置が合ってきたら数えるよ",
  ears: "耳が見えてきたら数えるよ",
};

/** Symmetry is judged across whichever of these planes the target is symmetric about. */
const MIRRORS = [
  { axis: 0, label: "左右のバランス", hint: "正面から見て左右をそろえてみよう" },
  { axis: 2, label: "前後のバランス", hint: "横から見て前後をそろえてみよう" },
];
/** Mirror errors this close count as equally symmetric (meshing noise). */
const MIRROR_TOLERANCE = 0.02;

/**
 * A piece whose hole sits where the target's does (gauge at least this full) while the shape
 * itself is still far off (base below this) is, topologically speaking, the very thing.
 */
const TOPOLOGIST = { holeFill: 0.7, maxBase: 80, title: "トポロジスト" };
const RANKS: [number, string, string][] = [
  [150, "SS", "伝説のこねこ職人！"],
  [120, "S", "こねこ名人！"],
  [100, "A", "かんせい！ おみごと！"],
  [70, "B", "いい感じ！"],
  [40, "C", "がんばった！"],
  [0, "D", "これからが楽しみ"],
];

/**
 * Progress toward the target (0 at the starting clay, 1 at a perfect answer) that fills
 * a main gauge: most of the way, with the rest left for the likeness bonus. A gauge whose
 * clay starts near the target fills sooner (see MAIN_TOLERANCE).
 */
const MAIN_FULL = 0.7;
/**
 * Error left over a perfect answer that still fills a main gauge, however near the target
 * the clay starts: kneading always leaves some unevenness, and where the clay has little
 * way to go (many holes, a flat plate), the same unevenness would otherwise cost far more
 * of the gauge.
 */
const MAIN_TOLERANCE: Errors = { silhouette: 0.17, balance: 0.11, contour: 0.075, hole: 0.2 };
/** The soonest a main gauge fills, so the clay still has some way to go. */
const MAIN_FULL_MIN = 0.4;
/** Progress that earns the whole balance bonus. */
const BONUS_FULL = 0.7;
/** A wish gauge this full (as earned) counts as met: the customer thanks the shop for it. */
const WISH_MET = 0.7;
/** A wish gauge this full already pleases the customer, who says so though it is not met. */
const WISH_PLEASED = 0.4;
/** The stroke bonus is full at the target's par and runs out at this many times it. */
const MAX_STROKES_PER_PAR = 3;

/** Silhouette raster resolution per view. */
const RES = 96;
/** Orthographic views as (horizontal, vertical) axis pairs: front, side, top. */
const VIEWS: [number, number][] = [[0, 1], [2, 1], [0, 2]];
/** What each view is called, for a piece that shows no hole in the one its target is judged in. */
const VIEW_NAMES = ["正面", "横", "上"];
/** Slices along the main axis for the width profile the waist is found in. */
const PROFILE_SLICES = 48;
/** Directions across the main axis each slice's width is measured in. */
const PROFILE_DIRECTIONS = 8;
/** A groove between coils lies below the widest slices this many slices away on either side. */
const COIL_REACH = 4;
/** Enclosed background smaller than this many pixels is raster noise, not a hole. */
const MIN_HOLE_PIXELS = 4;
/** Hole error when the clay shows no hole where the target has one. */
const MISSING_HOLE_ERROR = 2;
/**
 * A neck deeper than the target's counts this much of the shortfall a shallower one
 * would: it is still a waist, and pinching one tends to overshoot.
 */
const WAIST_OVERSHOOT = 0.5;
/** A neck this far off along the length (as a share of it) is as bad as none at all. */
const WAIST_POS_TOLERANCE = 0.25;

export interface CriterionResult {
  id: CriterionId;
  label: string;
  points: number;
  max: number;
  /** How full the gauge is, 0–1 (bonuses as far as the piece earns them). */
  fill: number;
  comment: string;
}

export interface Judgement {
  /** Base (up to 100) plus bonuses. */
  total: number;
  base: number;
  bonus: number;
  rank: string;
  title: string;
  /** The holes are right though the shape is not (see TOPOLOGIST). */
  topological: boolean;
  main: CriterionResult[];
  bonuses: CriterionResult[];
  /** The best points, to praise. Empty when nothing stands out yet. */
  good: CriterionResult[];
  /** Advice for the weakest point, if one stands out. */
  hint: string | null;
  /** The customer's wish came true. */
  wishMet: boolean;
  /** It came at least partway, which already pleases the customer. */
  wishPleased: boolean;
}

type MeasuredId = Exclude<CriterionId, "efficiency">;
/** Error measures, lower is better. Efficiency is scored from the stroke count instead. */
type Errors = Partial<Record<MeasuredId, number>>;

interface Extents {
  min: number[];
  max: number[];
}

interface Frame {
  u: number;
  v: number;
  min: [number, number];
  cell: number;
}

interface Balance {
  /** Volume centroid position along the main axis, 0 at one end and 1 at the other. */
  centroidPos: number;
  /** Length along the main axis over the mean width across it. */
  aspect: number;
}

interface Waist {
  /** Where along the length the neck is (0–1). */
  pos: number;
  /** How deep it is: 1 − neck width over the mean width of the bulges either side. */
  depth: number;
}

/** Through-hole seen in one view, placed as holeOf or holesOf says. */
interface Hole {
  area: number;
  u: number;
  v: number;
}

export function extentsOf(shape: Shape): Extents {
  const min = [Infinity, Infinity, Infinity], max = [-Infinity, -Infinity, -Infinity];
  const p = shape.positions, c = shape.centroid.toArray();
  for (let i = 0; i < p.length; i++) {
    const a = i % 3, v = p[i] - c[a];
    if (v < min[a]) min[a] = v;
    if (v > max[a]) max[a] = v;
  }
  return { min, max };
}

/** A square raster frame covering all given extents in the (u, v) plane. */
function viewFrame(u: number, v: number, ...all: Extents[]): Frame {
  const lo = [Math.min(...all.map((e) => e.min[u])), Math.min(...all.map((e) => e.min[v]))];
  const hi = [Math.max(...all.map((e) => e.max[u])), Math.max(...all.map((e) => e.max[v]))];
  const cell = Math.max(hi[0] - lo[0], hi[1] - lo[1]) / (RES - 2);
  return {
    u,
    v,
    min: [(lo[0] + hi[0]) / 2 - cell * RES / 2, (lo[1] + hi[1]) / 2 - cell * RES / 2],
    cell,
  };
}

/** Orthographic silhouette: pixel centres covered by any projected triangle. */
function rasterize(shape: Shape, f: Frame): Uint8Array {
  const grid = new Uint8Array(RES * RES);
  const p = shape.positions, index = shape.index;
  const c = shape.centroid.toArray();
  const px = (i: number) => (p[i * 3 + f.u] - c[f.u] - f.min[0]) / f.cell;
  const py = (i: number) => (p[i * 3 + f.v] - c[f.v] - f.min[1]) / f.cell;
  for (let t = 0; t < index.length; t += 3) {
    const ax = px(index[t]), ay = py(index[t]);
    const bx = px(index[t + 1]), by = py(index[t + 1]);
    const cx = px(index[t + 2]), cy = py(index[t + 2]);
    const area = (bx - ax) * (cy - ay) - (by - ay) * (cx - ax);
    if (Math.abs(area) < 1e-12) continue;
    const s = area > 0 ? 1 : -1;
    const x0 = Math.max(0, Math.ceil(Math.min(ax, bx, cx) - 0.5));
    const x1 = Math.min(RES - 1, Math.floor(Math.max(ax, bx, cx) - 0.5));
    const y0 = Math.max(0, Math.ceil(Math.min(ay, by, cy) - 0.5));
    const y1 = Math.min(RES - 1, Math.floor(Math.max(ay, by, cy) - 0.5));
    for (let y = y0; y <= y1; y++) {
      const qy = y + 0.5;
      for (let x = x0; x <= x1; x++) {
        const qx = x + 0.5;
        if (
          s * ((bx - ax) * (qy - ay) - (by - ay) * (qx - ax)) >= 0 &&
          s * ((cx - bx) * (qy - by) - (cy - by) * (qx - bx)) >= 0 &&
          s * ((ax - cx) * (qy - cy) - (ay - cy) * (qx - cx)) >= 0
        ) grid[y * RES + x] = 1;
      }
    }
  }
  return grid;
}

function iou(a: Uint8Array, b: Uint8Array): number {
  let both = 0, either = 0;
  for (let i = 0; i < a.length; i++) {
    both += a[i] & b[i];
    either += a[i] | b[i];
  }
  return either ? both / either : 1;
}

function balanceOf(shape: Shape, axis: number): Balance {
  const ext = extentsOf(shape);
  const length = ext.max[axis] - ext.min[axis];
  const across = [0, 1, 2].filter((a) => a !== axis).map((a) => ext.max[a] - ext.min[a]);
  return {
    centroidPos: -ext.min[axis] / length,
    aspect: length / ((across[0] + across[1]) / 2),
  };
}

/**
 * Finds the deepest neck in a width profile: the point whose width falls furthest below
 * the widest points on both sides of it. Its depth is measured against the mean of the two
 * bulges, so a small head on a big body (a gourd) still shows its neck.
 */
export function findWaist(width: number[]): Waist {
  const w = width.map((_, i) => ((width[i - 1] ?? 0) + 2 * width[i] + (width[i + 1] ?? 0)) / 4);
  const start = w.findIndex((x) => x > 0);
  const end = w.findLastIndex((x) => x > 0);
  if (end - start < 4) return { pos: 0.5, depth: 0 };
  const before = w.slice();
  for (let i = start + 1; i <= end; i++) before[i] = Math.max(before[i - 1], w[i]);
  const after = w.slice();
  for (let i = end - 1; i >= start; i--) after[i] = Math.max(after[i + 1], w[i]);
  let best = start, depth = 0;
  for (let i = start; i <= end; i++) {
    const d = Math.min(before[i], after[i]) - w[i];
    if (d > depth) {
      depth = d;
      best = i;
    }
  }
  if (depth <= 0) return { pos: 0.5, depth: 0 };
  return {
    pos: (best - start) / (end - start),
    depth: 1 - w[best] / ((before[best] + after[best]) / 2),
  };
}

/**
 * The width of each slice across the main axis in each of PROFILE_DIRECTIONS directions
 * (slice k's in direction j at k * PROFILE_DIRECTIONS + j), 0 in an empty slice.
 */
export function widthsOf(shape: Shape, axis: number): Float64Array {
  const p = shape.positions, n = p.length / 3;
  const [u, v] = [0, 1, 2].filter((a) => a !== axis);
  const ext = extentsOf(shape), lo = ext.min[axis] + shape.centroid.getComponent(axis);
  const length = ext.max[axis] - ext.min[axis];
  const dirs = Array.from({ length: PROFILE_DIRECTIONS }, (_, j) => {
    const angle = (Math.PI * j) / PROFILE_DIRECTIONS;
    return [Math.cos(angle), Math.sin(angle)];
  });
  const cells = PROFILE_SLICES * PROFILE_DIRECTIONS;
  const min = new Float64Array(cells).fill(Infinity), max = new Float64Array(cells).fill(-Infinity);
  for (let i = 0; i < n; i++) {
    const t = (p[i * 3 + axis] - lo) / length;
    const k = Math.min(PROFILE_SLICES - 1, Math.floor(t * PROFILE_SLICES));
    const x = p[i * 3 + u], y = p[i * 3 + v];
    for (let j = 0; j < PROFILE_DIRECTIONS; j++) {
      const d = x * dirs[j][0] + y * dirs[j][1], c = k * PROFILE_DIRECTIONS + j;
      if (d < min[c]) min[c] = d;
      if (d > max[c]) max[c] = d;
    }
  }
  return max.map((m, c) => (m > min[c] ? m - min[c] : 0));
}

/**
 * Deepest neck along the main axis. Each slice across the axis is measured in several
 * directions and its narrowest width counts, so a neck shows whichever way it was pinched
 * (from the sides only, the front only, or all round).
 */
function waistOf(shape: Shape, axis: number): Waist {
  const widths = widthsOf(shape, axis);
  return findWaist(Array.from({ length: PROFILE_SLICES }, (_, k) => {
    let narrowest = Infinity;
    for (let j = 0; j < PROFILE_DIRECTIONS; j++) {
      narrowest = Math.min(narrowest, widths[k * PROFILE_DIRECTIONS + j]);
    }
    return narrowest;
  }));
}

/**
 * How deep the grooves between coils wound round the main axis are cut: over the length,
 * the mean depth of each slice below the widest slices near it on both sides, as a share of
 * the mean width. A slice's width is its mean over the directions, as a groove goes all
 * round. A smooth shape has none, however it tapers or bulges.
 */
function coilsOf(shape: Shape, axis: number): number {
  const widths = widthsOf(shape, axis);
  const w = Array.from({ length: PROFILE_SLICES }, (_, k) => {
    let sum = 0;
    for (let j = 0; j < PROFILE_DIRECTIONS; j++) sum += widths[k * PROFILE_DIRECTIONS + j];
    return sum / PROFILE_DIRECTIONS;
  });
  let depth = 0, width = 0;
  for (let k = 0; k < PROFILE_SLICES; k++) {
    const before = Math.max(...w.slice(Math.max(0, k - COIL_REACH), k + 1));
    const after = Math.max(...w.slice(k, k + COIL_REACH + 1));
    depth += Math.min(before, after) - w[k];
    width += w[k];
  }
  return width ? depth / width : 0;
}

/**
 * Background pixels the silhouette encloses (not reachable from the border) are where one can
 * see through the shape: 1 there, in the frame `f` of the view. The silhouette comes too.
 */
function enclosedOf(shape: Shape, f: Frame): { grid: Uint8Array; enclosed: Uint8Array } {
  const grid = rasterize(shape, f);
  const reached = new Uint8Array(RES * RES);
  const stack: number[] = [];
  const visit = (x: number, y: number) => {
    const i = y * RES + x;
    if (grid[i] || reached[i]) return;
    reached[i] = 1;
    stack.push(i);
  };
  for (let k = 0; k < RES; k++) {
    visit(k, 0);
    visit(k, RES - 1);
    visit(0, k);
    visit(RES - 1, k);
  }
  while (stack.length) {
    const i = stack.pop()!, x = i % RES, y = (i - x) / RES;
    if (x > 0) visit(x - 1, y);
    if (x < RES - 1) visit(x + 1, y);
    if (y > 0) visit(x, y - 1);
    if (y < RES - 1) visit(x, y + 1);
  }
  return { grid, enclosed: grid.map((g, i) => (g || reached[i] ? 0 : 1)) };
}

/**
 * Where one can see through the shape, all its holes taken together as one, placed from the
 * middle of the outline round them (the silhouette with its holes filled in). The middle of
 * the volume would do as well for a solid piece, but a flat plate's moves with any unevenness
 * in its thickness, which the view does not show.
 */
function holeOf(shape: Shape, [u, v]: [number, number]): Hole | null {
  const f = viewFrame(u, v, extentsOf(shape));
  const { grid, enclosed } = enclosedOf(shape, f);
  let count = 0, su = 0, sv = 0, outline = 0, ou = 0, ov = 0;
  for (let i = 0; i < enclosed.length; i++) {
    if (!grid[i] && !enclosed[i]) continue;
    const x = i % RES, y = Math.floor(i / RES);
    outline++;
    ou += x;
    ov += y;
    if (!enclosed[i]) continue;
    count++;
    su += x;
    sv += y;
  }
  if (count < MIN_HOLE_PIXELS) return null;
  return {
    area: count * f.cell * f.cell,
    u: (su / count - ou / outline) * f.cell,
    v: (sv / count - ov / outline) * f.cell,
  };
}

/** Each hole one can see through the shape, apart (raster noise left out). */
function holesOf(shape: Shape, [u, v]: [number, number]): Hole[] {
  const f = viewFrame(u, v, extentsOf(shape));
  const { enclosed } = enclosedOf(shape, f);
  const holes: Hole[] = [];
  const stack: number[] = [];
  for (let start = 0; start < enclosed.length; start++) {
    if (enclosed[start] !== 1) continue;
    let count = 0, su = 0, sv = 0;
    enclosed[start] = 2;
    stack.push(start);
    while (stack.length) {
      const i = stack.pop()!, x = i % RES, y = (i - x) / RES;
      count++;
      su += x;
      sv += y;
      for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]]) {
        if (nx < 0 || ny < 0 || nx >= RES || ny >= RES) continue;
        const j = ny * RES + nx;
        if (enclosed[j] !== 1) continue;
        enclosed[j] = 2;
        stack.push(j);
      }
    }
    if (count < MIN_HOLE_PIXELS) continue;
    holes.push({
      area: count * f.cell * f.cell,
      u: f.min[0] + (su / count + 0.5) * f.cell,
      v: f.min[1] + (sv / count + 0.5) * f.cell,
    });
  }
  return holes;
}

/**
 * How far apart the holes lie: the mean distance of each hole's centre from the middle of
 * them all, in the view the holes are judged in. 0 with fewer than two holes to be seen.
 */
function spreadOf(shape: Shape, view: [number, number]): number {
  const holes = holesOf(shape, view);
  if (holes.length < 2) return 0;
  const mu = holes.reduce((t, h) => t + h.u, 0) / holes.length;
  const mv = holes.reduce((t, h) => t + h.v, 0) / holes.length;
  return holes.reduce((t, h) => t + Math.hypot(h.u - mu, h.v - mv), 0) / holes.length;
}

/**
 * How deep the front outline's waist is: along the longer side of the front silhouette, the
 * span between the outermost filled pixels across it, and the deepest neck in that (see
 * findWaist). Unlike the waist, it sees a flat piece's outline (a panel in the shape of a
 * peanut), whose thickness would otherwise be the narrowest width everywhere.
 */
function neckOf(shape: Shape): number {
  const grid = rasterize(shape, viewFrame(0, 1, extentsOf(shape)));
  let x0 = RES, x1 = -1, y0 = RES, y1 = -1;
  for (let i = 0; i < grid.length; i++) {
    if (!grid[i]) continue;
    const x = i % RES, y = (i - x) / RES;
    x0 = Math.min(x0, x);
    x1 = Math.max(x1, x);
    y0 = Math.min(y0, y);
    y1 = Math.max(y1, y);
  }
  if (x1 < 0) return 0;
  const along = x1 - x0 >= y1 - y0;
  const spans = Array.from({ length: RES }, (_, k) => {
    let lo = -1, hi = -1;
    for (let j = 0; j < RES; j++) {
      if (!grid[along ? j * RES + k : k * RES + j]) continue;
      if (lo < 0) lo = j;
      hi = j;
    }
    return lo < 0 ? 0 : hi - lo + 1;
  });
  return findWaist(spans).depth;
}

/**
 * How square the piece is from above: the share of the box round its silhouette that the
 * silhouette fills. A round body leaves the box's corners empty; a boxy one fills them.
 */
function squareOf(shape: Shape): number {
  const grid = rasterize(shape, viewFrame(0, 2, extentsOf(shape)));
  let x0 = RES, x1 = -1, y0 = RES, y1 = -1, filled = 0;
  for (let i = 0; i < grid.length; i++) {
    if (!grid[i]) continue;
    const x = i % RES, y = (i - x) / RES;
    x0 = Math.min(x0, x);
    x1 = Math.max(x1, x);
    y0 = Math.min(y0, y);
    y1 = Math.max(y1, y);
    filled++;
  }
  return x1 < 0 ? 0 : filled / ((x1 - x0 + 1) * (y1 - y0 + 1));
}

/**
 * How far apart the legs stand: over the lower half of the front silhouette, the mean share
 * of each row's width left empty between its outermost filled pixels.
 */
function splitOf(shape: Shape): number {
  const grid = rasterize(shape, viewFrame(0, 1, extentsOf(shape)));
  let bottom = RES, top = -1;
  for (let i = 0; i < grid.length; i++) {
    if (!grid[i]) continue;
    const y = Math.floor(i / RES);
    bottom = Math.min(bottom, y);
    top = Math.max(top, y);
  }
  let sum = 0, rows = 0;
  for (let y = bottom; y <= (bottom + top) / 2; y++) {
    let left = -1, right = -1, filled = 0;
    for (let x = 0; x < RES; x++) {
      if (!grid[y * RES + x]) continue;
      if (left < 0) left = x;
      right = x;
      filled++;
    }
    if (left < 0) continue;
    sum += 1 - filled / (right - left + 1);
    rows++;
  }
  return rows ? sum / rows : 0;
}

/**
 * How far ears stand up on top: in the front silhouette, how far the top outline's highest
 * point on each side of the middle stands above that side's outline on average, the two
 * sides averaged.
 */
function earsOf(shape: Shape): number {
  const f = viewFrame(0, 1, extentsOf(shape));
  const grid = rasterize(shape, f);
  const tops = Array.from({ length: RES }, (_, x) => {
    for (let y = RES - 1; y >= 0; y--) if (grid[y * RES + x]) return y;
    return -1;
  });
  const left = tops.findIndex((t) => t >= 0), right = tops.findLastIndex((t) => t >= 0);
  if (left < 0) return 0;
  const middle = (left + right) / 2;
  const sides = [
    tops.slice(left, Math.floor(middle) + 1),
    tops.slice(Math.ceil(middle), right + 1),
  ];
  let sum = 0;
  for (const side of sides.map((s) => s.filter((t) => t >= 0))) {
    sum += Math.max(...side) - side.reduce((a, b) => a + b, 0) / side.length;
  }
  return (sum / 2) * f.cell;
}

/** Mean distance between the shape and its mirror image across a plane through its centroid. */
export function asymmetryOf(shape: Shape, axis: number): number {
  const p = shape.positions, c = shape.centroid.toArray();
  const centred = new Float32Array(p.length);
  for (let i = 0; i < p.length; i++) centred[i] = p[i] - c[i % 3];
  const tree = new PointTree();
  tree.build(centred);
  const q = [0, 0, 0];
  let sum = 0, weight = 0;
  for (let i = 0; i < shape.areas.length; i++) {
    q[0] = centred[i * 3];
    q[1] = centred[i * 3 + 1];
    q[2] = centred[i * 3 + 2];
    q[axis] = -q[axis];
    sum += shape.areas[i] * tree.nearestDistance(q[0], q[1], q[2]);
    weight += shape.areas[i];
  }
  return sum / weight;
}

/**
 * Smallest error span between a perfect answer and quality 0.2. The clay starts nearly
 * symmetric, so calibrating symmetry on it would punish the slightest lean. A square plate's
 * clay starts already balanced and proportioned, and some clays' holes already sit nearly
 * where the target's do, so those gauges are calibrated as if the clay started just far
 * enough off to fill at MAIN_TOLERANCE: the untouched clay earns them.
 */
const MIN_SPAN: Errors = {
  symmetry: 0.1,
  ears: 0.1,
  balance: MAIN_TOLERANCE.balance! / (1 - MAIN_FULL_MIN),
  hole: MAIN_TOLERANCE.hole! / (1 - MAIN_FULL_MIN),
};

/** 0 at or beyond `start` (the untouched clay), 1 at or below `floor` (a perfect answer). */
function progressOf(error: number, start: number, floor: number, minSpan = 0): number {
  const span = Math.max(start - floor, minSpan, 1e-6);
  return Math.min(1, Math.max(0, 1 - Math.max(0, error - floor) / span));
}

function commentFor(c: Criterion, level: number): string {
  if (level >= 0.99) return c.comments[0];
  if (level >= 0.7) return c.comments[1];
  if (level >= 0.4) return c.comments[2];
  return c.comments[3];
}

/**
 * Feature-based, praise-first scoring against one target. Every measure is calibrated per
 * target as progress from the untouched clay (0) to the target itself meshed at the
 * clay's resolution (1). The shape's three gauges and its feature make up 100 points;
 * bonuses go beyond.
 */
export class Judge {
  private readonly criteria: Record<CriterionId, Criterion>;
  private readonly ids: CriterionId[];
  private readonly axis: number;
  /** Axis of the plane the target is symmetric about (0 = x, left-right; 2 = z, front-back). */
  readonly mirrorAxis: number;
  private readonly holeView: [number, number];
  private readonly maxStrokes: number;
  private readonly targetShape: Shape;
  private readonly targetExtents: Extents;
  private readonly targetBalance: Balance;
  private readonly targetWaist: Waist = { pos: 0.5, depth: 0 };
  private readonly targetHole: Hole | null = null;
  private readonly targetEars: number = 0;
  private readonly targetAsymmetry: number;
  private readonly start: Errors;
  private readonly floor: Errors;
  /** Progress that fills each main gauge (see MAIN_FULL and MAIN_TOLERANCE). */
  private readonly full: Errors = {};
  /** The target as the clay could represent it (see Target.referenceMesh). */
  private readonly reference: Shape;
  /** The same of the usual target, when the one judged is a customer's variant (see Wish). */
  private readonly plain: Shape | null;

  constructor(
    private readonly target: Target,
    private readonly scorer: Scorer,
    initial: Shape,
    reference: Shape,
    plain?: Shape,
  ) {
    const { feature } = target.def;
    this.ids = [...SHAPE_IDS, feature, "likeness", "symmetry", "efficiency"];
    this.axis = target.mainAxis;
    this.maxStrokes = target.def.par * MAX_STROKES_PER_PAR;
    this.targetShape = {
      positions: target.positions,
      index: target.index,
      areas: target.areas,
      centroid: new THREE.Vector3(),
    };
    this.targetExtents = extentsOf(this.targetShape);
    this.targetBalance = balanceOf(this.targetShape, this.axis);

    // Prefer left-right; switch planes only when the target is clearly lopsided that way.
    const mirrors = MIRRORS.map((m) => ({ ...m, error: asymmetryOf(this.targetShape, m.axis) }));
    const least = Math.min(...mirrors.map((m) => m.error));
    const mirror = mirrors.find((m) => m.error <= least + MIRROR_TOLERANCE)!;
    this.mirrorAxis = mirror.axis;
    this.targetAsymmetry = mirror.error;
    this.criteria = {
      ...CRITERIA,
      symmetry: { ...CRITERIA.symmetry, label: mirror.label, hint: mirror.hint },
    };

    this.holeView = VIEWS[0];
    if (feature === "waist") this.targetWaist = waistOf(this.targetShape, this.axis);
    if (feature === "hole") {
      // Judge the hole in the view where the target shows it largest.
      const holes = VIEWS.map((view) => ({ view, hole: holeOf(this.targetShape, view) }));
      const best = holes.reduce((a, b) => ((b.hole?.area ?? 0) > (a.hole?.area ?? 0) ? b : a));
      this.holeView = best.view;
      this.targetHole = best.hole;
    }
    if (feature === "ears") this.targetEars = earsOf(this.targetShape);

    this.start = this.errors(initial);
    this.floor = this.errors(reference);
    for (const id of [...SHAPE_IDS, feature] as MeasuredId[]) {
      const span = Math.max(this.start[id]! - this.floor[id]!, MIN_SPAN[id] ?? 0);
      const tolerance = MAIN_TOLERANCE[id] ?? 0;
      this.full[id] = Math.max(MAIN_FULL_MIN, Math.min(MAIN_FULL, 1 - tolerance / span));
    }
    this.reference = reference;
    this.plain = plain ?? null;
  }

  judge(shape: Shape, strokes: number, wish?: Wish): Judgement {
    const errors = this.errors(shape);
    const progress: Partial<Record<MeasuredId, number>> = {};
    for (const id of Object.keys(errors) as MeasuredId[]) {
      progress[id] = progressOf(errors[id]!, this.start[id]!, this.floor[id]!, MIN_SPAN[id]);
    }

    const fill: Partial<Record<CriterionId, number>> = {};
    const { feature, par } = this.target.def;
    let likeness = 0, shapeMax = 0;
    for (const id of SHAPE_IDS) {
      const p = progress[id as MeasuredId]!, full = this.full[id as MeasuredId]!;
      const max = this.criteria[id].max;
      fill[id] = Math.min(1, p / full);
      likeness += max * Math.max(0, p - full) / (1 - full);
      shapeMax += max;
    }
    fill.likeness = likeness / shapeMax;
    fill[feature] = Math.min(1, progress[feature]! / this.full[feature as MeasuredId]!);
    fill.symmetry = Math.min(1, progress.symmetry! / BONUS_FULL);
    const brevity = Math.min(1, Math.max(0, (this.maxStrokes - strokes) / (this.maxStrokes - par)));
    fill.efficiency = brevity;

    const mainIds = this.ids.filter((id) => !this.criteria[id].bonus);
    // The clay may start with a hole, only not where the target has one: the gauge alone
    // cannot tell that from no hole at all.
    const noHole = errors.hole === MISSING_HOLE_ERROR
      ? `${VIEW_NAMES[VIEWS.indexOf(this.holeView)]}から見て穴が見えないかも`
      : undefined;
    const main = mainIds.map((id) =>
      this.result(id, fill[id]!, 1, strokes, id === "hole" ? noHole : undefined)
    );
    const base = main.reduce((t, c) => t + c.points, 0);
    const mainMax = mainIds.reduce((t, id) => t + this.criteria[id].max, 0);
    // Bonuses count for as much as the piece is worth, so an untouched clay earns none. Balance
    // and few strokes count only as far as the feature is there too: an egg for a snowman is
    // neither well balanced nor quickly made.
    const worth = base / mainMax, free = worth * fill[feature]!;
    const pending = fill[feature]! < 0.4 ? FEATURE_PENDING[feature] : undefined;
    const bonuses = this.ids.filter((id) => this.criteria[id].bonus).map((id) =>
      id === "likeness"
        ? this.result(id, fill[id]!, worth, strokes)
        : this.result(id, fill[id]!, free, strokes, pending)
    );
    // The wish counts like balance and few strokes: only for a piece that is what was ordered.
    const wishCriterion = wish &&
      { ...CRITERIA.wish, label: `こだわり: ${wish.label}`, hint: wish.hint };
    if (wishCriterion) {
      bonuses.push(
        this.result("wish", this.wishFill(shape, wish), free, strokes, pending, wishCriterion),
      );
    }
    const wishFill = bonuses.find((c) => c.id === "wish")?.fill ?? 0;
    const wishMet = wishFill >= WISH_MET, wishPleased = wishFill >= WISH_PLEASED;
    const bonus = bonuses.reduce((t, c) => t + c.points, 0);
    const total = base + bonus;
    const topological = feature === "hole" && fill.hole! >= TOPOLOGIST.holeFill &&
      base < TOPOLOGIST.maxBase;
    const [, rank, rankTitle] = RANKS.find(([min]) => total >= min)!;
    const title = topological ? TOPOLOGIST.title : rankTitle;

    const praisable = [...main, ...bonuses].filter((c) => c.points > 0);
    const good = praisable.filter((c) => c.fill >= 0.7).sort((a, b) => b.fill - a.fill).slice(0, 2);
    if (good.length === 0) {
      const best = praisable.sort((a, b) => b.fill - a.fill)[0];
      if (best && best.fill >= 0.4) good.push(best);
    }
    const weakMain = main.filter((c) => c.fill < 1).sort((a, b) => a.fill - b.fill)[0];
    const weakBonus = bonuses
      .filter((c) => c.id !== "likeness" && (c.id !== "efficiency" || brevity < 0.5))
      .filter((c) => c.fill < BONUS_FULL)
      .sort((a, b) => a.fill - b.fill)[0];
    const weakest = weakMain ?? weakBonus;
    const hint = !weakest || good.includes(weakest)
      ? null
      : weakest.id === "wish"
      ? wishCriterion!.hint
      : this.criteria[weakest.id].hint;

    return {
      total,
      base,
      bonus,
      rank,
      title,
      topological,
      main,
      bonuses,
      good,
      hint,
      wishMet,
      wishPleased,
    };
  }

  /** How far the piece goes from the usual target to the customer's variant, 0–1. */
  wishFill(shape: Shape, { measure }: Wish): number {
    if (!this.plain) return 0;
    const from = this.wishMeasure(this.plain, measure);
    const to = this.wishMeasure(this.reference, measure);
    if (Math.abs(to - from) < 1e-6) return 0;
    return Math.min(1, Math.max(0, (this.wishMeasure(shape, measure) - from) / (to - from)));
  }

  private wishMeasure(shape: Shape, measure: Wish["measure"]): number {
    if (measure === "aspect") return balanceOf(shape, this.axis).aspect;
    if (measure === "waist") return waistOf(shape, this.axis).depth;
    if (measure === "hole") return holeOf(shape, this.holeView)?.area ?? 0;
    if (measure === "coils") return coilsOf(shape, this.axis);
    if (measure === "ears") return earsOf(shape);
    if (measure === "neck") return neckOf(shape);
    if (measure === "spread") return spreadOf(shape, this.holeView);
    if (measure === "square") return squareOf(shape);
    return splitOf(shape);
  }

  private result(
    id: CriterionId,
    fill: number,
    worth: number,
    strokes: number,
    pending?: string,
    c = this.criteria[id],
  ): CriterionResult {
    const earned = fill * worth, comment = pending ?? commentFor(c, fill);
    return {
      id,
      label: c.label,
      max: c.max,
      points: Math.round(c.max * earned),
      fill: earned,
      comment: comment.replace("{n}", String(strokes)),
    };
  }

  private errors(shape: Shape): Errors {
    const ext = extentsOf(shape);
    const silhouette = 1 -
      VIEWS.reduce((s, [u, v]) => {
          const frame = viewFrame(u, v, ext, this.targetExtents);
          return s + iou(rasterize(shape, frame), rasterize(this.targetShape, frame));
        }, 0) / VIEWS.length;

    const b = balanceOf(shape, this.axis), tb = this.targetBalance;
    const errors: Errors = {
      silhouette,
      balance: 2 * Math.abs(b.centroidPos - tb.centroidPos) +
        Math.abs(Math.log(b.aspect / tb.aspect)),
      contour: this.scorer.distance(shape.positions, shape.areas, shape.centroid),
      symmetry: Math.max(0, asymmetryOf(shape, this.mirrorAxis) - this.targetAsymmetry),
    };

    if (this.target.def.feature === "waist") {
      // 1 for no neck, 0 for the target's. Too deep is forgiven more than too shallow, and
      // a shallow neck in the wrong place matters less than a deep one.
      const w = waistOf(shape, this.axis), tw = this.targetWaist;
      const target = Math.max(tw.depth, 1e-3);
      const depth = w.depth < target
        ? (target - w.depth) / target
        : WAIST_OVERSHOOT * (w.depth - target) / Math.max(1 - target, 1e-3);
      const place = Math.abs(w.pos - tw.pos) / WAIST_POS_TOLERANCE;
      errors.waist = depth + place * Math.min(1, w.depth / target);
    } else if (this.target.def.feature === "hole") {
      const h = holeOf(shape, this.holeView), th = this.targetHole;
      errors.hole = !th ? 0 : !h ? MISSING_HOLE_ERROR : Math.hypot(h.u - th.u, h.v - th.v) +
        0.5 * Math.abs(Math.log(h.area / th.area));
    } else {
      errors.ears = Math.abs(earsOf(shape) - this.targetEars);
    }
    return errors;
  }
}
